import { useMemo } from 'react'
import { HeroSection } from '@/components/sections/HeroSection'
import { Marquee } from '@/components/sections/Marquee'
import { DropCountdown } from '@/components/sections/DropCountdown'
import { StreakStrip } from '@/components/sections/StreakStrip'
import { MidnightMarket } from '@/components/sections/MidnightMarket'
import { IntroSection } from '@/components/sections/IntroSection'
import { HomeCategories } from '@/components/sections/HomeCategories'
import { ProductGrid } from '@/components/sections/ProductGrid'
import { HorizontalShowcase } from '@/components/sections/HorizontalShowcase'
import { ScentQuizTeaser } from '@/components/sections/ScentQuizTeaser'
import { JournalTeasers } from '@/components/sections/JournalTeasers'
import { PressMarquee } from '@/components/sections/PressMarquee'
import { StatsBar } from '@/components/sections/StatsBar'
import { Testimonials } from '@/components/sections/Testimonials'
import { useHomeData } from '@/hooks/useHomeData'
import { useSettings } from '@/hooks/useSettings'
import type { Perfume, Collection, JournalArticle } from '@/types'

function mapHomeProductToPerfume(p: {
  id: string
  slug: string
  name: string
  family: string
  concentration: string
  price: number
  price_kobo: number
  isNew: boolean
  isBestseller: boolean
  isDiscoverySet: boolean
  labels: ('new' | 'bestseller' | 'discovery-set' | 'refill')[]
  image: string
  sizes: {
    size: string
    price: number
    price_kobo: number
    isRefillable: boolean
    inStock: boolean
  }[]
}): Perfume {
  return {
    id: p.slug,
    apiId: Number(p.id),
    name: p.name,
    family: p.family as Perfume['family'],
    concentration: p.concentration as Perfume['concentration'],
    description: '',
    story: '',
    notes: { top: [], heart: [], base: [] },
    images: [p.image],
    badges: [],
    rating: 0,
    reviewCount: 0,
    isNew: p.isNew,
    isBestseller: p.isBestseller,
    isDiscoverySet: p.isDiscoverySet,
    labels: p.labels,
    sizes: p.sizes.map((s, i) => ({
      id: `${p.slug}-${i}`,
      size: s.size,
      price: s.price,
      isRefillable: s.isRefillable,
      inStock: s.inStock,
    })),
    occasionTags: [],
    smellsLike: [],
  }
}

function mapHomeCollectionToCollection(c: {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  image: string
  perfumeIds: number[]
}): Collection {
  return {
    id: c.slug,
    slug: c.slug,
    name: c.name,
    tagline: c.tagline,
    description: c.description,
    image: c.image,
    perfumeIds: c.perfumeIds.map((id) => String(id)),
  }
}

function mapHomeArticleToJournalArticle(a: {
  slug: string
  title: string
  excerpt: string
  body: string
  image: string
  publishedAt: string | null
}): JournalArticle {
  return {
    slug: a.slug,
    title: a.title,
    excerpt: a.excerpt,
    readTime: 3,
    image: a.image,
    body: a.body,
    publishedAt: a.publishedAt ?? '',
  }
}

export function Home() {
  const { homeData } = useHomeData()
  const { content_visibility } = useSettings()

  const perfumes = useMemo(
    () => (homeData?.bestsellers ?? []).map(mapHomeProductToPerfume),
    [homeData?.bestsellers]
  )

  const allHomeProducts = useMemo(
    () => [
      ...(homeData?.bestsellers ?? []),
      ...(homeData?.newest ?? []),
    ].map(mapHomeProductToPerfume),
    [homeData?.bestsellers, homeData?.newest]
  )

  const bestsellers = useMemo(
    () => perfumes.filter((p) => p.isBestseller),
    [perfumes]
  )

  const collections = useMemo(
    () => (homeData?.collections ?? []).map(mapHomeCollectionToCollection),
    [homeData?.collections]
  )

  const articles = useMemo(
    () => (homeData?.articles ?? []).map(mapHomeArticleToJournalArticle),
    [homeData?.articles]
  )

  const promos = homeData?.promos

  return (
    <div className="overflow-x-[clip]">
      {content_visibility?.showHero !== false && <HeroSection />}
      <Marquee />

      <DropCountdown />
      <StreakStrip />

      <MidnightMarket promo={promos?.midnightMarket} products={allHomeProducts} />

      <IntroSection />

      {content_visibility?.showCategories !== false && <HomeCategories />}

      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-content px-6 lg:px-10">
          <div className="mx-auto mb-16 max-w-text-block text-center">
            <p className="mb-4 font-condensed text-xs uppercase tracking-[0.2em] text-wd-orange">
              Bestsellers
            </p>
            <h2 className="font-display text-display-lg lg:text-display-xl font-normal normal-case leading-[0.95] text-carbon">
              House signatures
            </h2>
          </div>
          <ProductGrid perfumes={bestsellers} />
        </div>
      </section>

      {content_visibility?.showCollections !== false && (
        <div id="collections">
          <HorizontalShowcase
            title="the collections"
            subtitle="curated worlds"
            collections={collections}
          />
        </div>
      )}

      {content_visibility?.showScentQuiz !== false && <ScentQuizTeaser />}

      {content_visibility?.showJournalTeasers !== false && (
        <JournalTeasers articles={articles} />
      )}

      {content_visibility?.showPressMarquee !== false && <PressMarquee />}

      {content_visibility?.showTestimonials !== false && <Testimonials />}

      <StatsBar />
    </div>
  )
}
